import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { colors } from "../../utils/Colors";
import Paths from "../../utils/Paths";
import axios from "../../api/axios";

const c = colors.color;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [syncing, setSyncing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setSyncing(false);
      return;
    }

    axios
      .get(`/subscriptions/sync-session?session_id=${sessionId}`)
      .then(() => setSyncing(false))
      .catch((err) => {
        console.error("Sync error:", err);
        setError("Could not verify your subscription. Please contact support if the issue persists.");
        setSyncing(false);
      });
  }, [searchParams]);

  return (
    <>
      <Header />

      <div
        style={{
          background: `linear-gradient(160deg, #f0fff8 0%, #fff 50%, ${c.secondary}08 100%)`,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 16px",
          }}
        >
          <div style={{ maxWidth: 560, width: "100%", textAlign: "center" }}>

            {syncing ? (
              <>
                {/* Loading state */}
                <div
                  style={{
                    width: 80,
                    height: 80,
                    border: `6px solid ${c.secondary}33`,
                    borderTop: `6px solid ${c.secondary}`,
                    borderRadius: "50%",
                    margin: "0 auto 28px",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <h2 style={{ color: c.primary, fontWeight: 700 }}>Activating your subscription...</h2>
                <p style={{ color: c.darkGrey }}>Please wait, we are syncing your payment.</p>
              </>
            ) : error ? (
              <>
                {/* Error state */}
                <div style={{ fontSize: 60, marginBottom: 20 }}>⚠️</div>
                <h2 style={{ color: "#f46a6a", fontWeight: 700 }}>Something went wrong</h2>
                <p style={{ color: c.darkGrey, marginBottom: 24 }}>{error}</p>
                <button
                  onClick={() => navigate(Paths.subscriptionPlans)}
                  style={{
                    padding: "12px 32px",
                    borderRadius: 12,
                    border: "none",
                    background: `linear-gradient(135deg, ${c.secondary}, #00a67e)`,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: "pointer",
                  }}
                >
                  View My Plans
                </button>
              </>
            ) : (
              <>
                {/* Success state */}
                <div
                  style={{
                    width: 110,
                    height: 110,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #e6fff4, #ccffe9)",
                    border: `3px solid ${c.secondary}33`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 28px",
                    boxShadow: `0 8px 32px ${c.secondary}30`,
                  }}
                >
                  <span style={{ fontSize: 52 }}>🎉</span>
                </div>

                <h1
                  style={{
                    fontSize: "clamp(24px, 5vw, 36px)",
                    fontWeight: 800,
                    color: c.primary,
                    marginBottom: 12,
                    lineHeight: 1.2,
                  }}
                >
                  Subscription Activated!
                </h1>

                <p
                  style={{
                    fontSize: 16,
                    color: c.darkGrey,
                    lineHeight: 1.7,
                    maxWidth: 420,
                    margin: "0 auto 32px",
                  }}
                >
                  Your plan is now active. You're all set to start posting jobs and finding the{" "}
                  <strong style={{ color: c.secondary }}>perfect candidates</strong> for your team.
                </p>

                {/* Info cards */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: 14,
                    marginBottom: 36,
                  }}
                >
                  {[
                    { icon: "✅", title: "Plan Active", desc: "Your subscription is live" },
                    { icon: "📋", title: "Post Jobs", desc: "Start posting right away" },
                    { icon: "🔒", title: "Secure", desc: "Powered by Stripe" },
                  ].map((item) => (
                    <div
                      key={item.title}
                      style={{
                        background: "#fff",
                        borderRadius: 14,
                        padding: "18px 14px",
                        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                        border: `1px solid ${c.lightGrey}`,
                      }}
                    >
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{item.icon}</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: c.primary, marginBottom: 4 }}>
                        {item.title}
                      </div>
                      <div style={{ fontSize: 12, color: c.darkGrey, lineHeight: 1.5 }}>
                        {item.desc}
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                  <button
                    onClick={() => navigate(Paths.JobCreation)}
                    style={{
                      padding: "13px 36px",
                      borderRadius: 12,
                      border: "none",
                      background: `linear-gradient(135deg, ${c.secondary}, #00a67e)`,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: "pointer",
                      boxShadow: `0 6px 20px ${c.secondary}44`,
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                  >
                    📝 Post a Job
                  </button>
                </div>

                <p style={{ marginTop: 28, fontSize: 13, color: c.darkGrey }}>
                  Want to manage your subscription?{" "}
                  <span
                    style={{ color: c.secondary, fontWeight: 600, cursor: "pointer" }}
                    onClick={() => navigate(Paths.subscriptionPlans)}
                  >
                    View plans →
                  </span>
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SubscriptionSuccess;